import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type { EFastWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("efast");

const VIEW_OPTIONS = [
  { key: "ruq", label: "RUQ" },
  { key: "luq", label: "LUQ" },
  { key: "suprapubic", label: "Suprapubic" },
  { key: "subxiphoid", label: "Subxiphoid" },
  { key: "rLung", label: "R Lung" },
  { key: "lLung", label: "L Lung" },
] as const;

const FINDING_OPTIONS = [
  { value: "absent", label: "Absent" },
  { value: "present", label: "Present" },
  { value: "indeterminate", label: "Indeterminate" },
  { value: "not_assessed", label: "Not Assessed" },
] as const;

const FINDING_ROWS = [
  { key: "ruqFreeFluid", label: "RUQ Free Fluid" },
  { key: "luqFreeFluid", label: "LUQ Free Fluid" },
  { key: "pelvicFreeFluid", label: "Pelvic Free Fluid" },
  { key: "pericardialEffusion", label: "Pericardial Effusion" },
  { key: "rPneumothorax", label: "R Pneumothorax" },
  { key: "lPneumothorax", label: "L Pneumothorax" },
] as const;

export const isEfastComplete = (data: EFastWorksheetData): boolean =>
  Object.values(data.views).some(Boolean);

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

export const EfastWorksheet: FC<{
  data: EFastWorksheetData;
  onChange: (data: EFastWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateFindings = (patch: Partial<EFastWorksheetData["findings"]>) => {
    onChange({ ...data, findings: { ...data.findings, ...patch } });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as EFastWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <WorksheetSection label="Findings">
            {FINDING_ROWS.map((row) => (
              <Box key={row.key}>
                <FindingLabel>{row.label}</FindingLabel>
                <EnumChipSelect
                  options={[...FINDING_OPTIONS]}
                  selected={data.findings[row.key]}
                  onChange={(v) =>
                    updateFindings({
                      [row.key]: (v ??
                        "not_assessed") as EFastWorksheetData["findings"][typeof row.key],
                    })
                  }
                  disabled={readOnly}
                />
              </Box>
            ))}
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
